import { useCallback, useEffect, useMemo, useState } from "react";
import { listInboundByStaffRecipient } from "../services/emailCorrespondenceApi";
import { resolveEmailPlatformOrganizationGuid } from "../utils/emailPlatformOrganization";
import {
  apiOk,
  buildInboxRows,
  unreadInboxRows,
} from "../utils/notificationInboxUtils";

const INBOX_FETCH_TAKE = 200;
const REFRESH_MS = 60_000;

/** Shared unread inbox count for header bell + sidebar notifications menu. */
export default function useInboxUnreadCount(userData) {
  const [rows, setRows] = useState([]);

  const organizationRefId = useMemo(
    () => resolveEmailPlatformOrganizationGuid(userData),
    [userData],
  );

  const staffEmail = useMemo(
    () => String(userData?.EmailId || userData?.emailId || "").trim(),
    [userData?.EmailId, userData?.emailId],
  );

  const loadInbox = useCallback(async () => {
    if (!organizationRefId) {
      setRows([]);
      return;
    }
    try {
      const res = await listInboundByStaffRecipient({
        organizationRefId,
        take: INBOX_FETCH_TAKE,
      });
      if (!apiOk(res)) {
        setRows([]);
        return;
      }
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRows([]);
    }
  }, [organizationRefId]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!organizationRefId) return undefined;
    const timer = window.setInterval(loadInbox, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [organizationRefId, loadInbox]);

  const unreadCount = useMemo(
    () => unreadInboxRows(buildInboxRows(rows, staffEmail)).length,
    [rows, staffEmail],
  );
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  return { unreadCount, badgeLabel, refresh: loadInbox };
}
